// @flow

import * as React from 'react';

import { Download } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { compose } from 'redux';
import { Skeleton, Text, View } from 'reshaped';

import { withPageMeta } from '@gandi/app-runner';
import { StatusIcon, useOrder } from '@gandi/billing';
import { getCartUUIDFromOrderId } from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';
import { Button } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { CallToAction } from 'src/Overview/components/CallToAction';
import { Layout } from 'src/components/Layout';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectOrderUrl, selectAdminURL } from 'src/modules/config';
import { pathjoin } from 'src/modules/pathjoin';

export type Params = {
  orderId: string,
};

function AwaitingPayment() {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const params = useParams<Params>();

  const adminUrl = useAppSelector(selectAdminURL);
  const sharingId = useAppSelector(selectCurrentId);

  const [cartId, setCartId] = React.useState<string>('');
  const orderUrl = useAppSelector(state =>
    selectOrderUrl(state, { orderId: params.orderId }),
  );
  const { isFetching, order } = useOrder(Number(params.orderId));

  React.useEffect(() => {
    if (params.orderId) {
      dispatch(getCartUUIDFromOrderId(params.orderId)).then(resp => {
        const cartUUID = resp.data?.[0]?.uuid;
        if (!cartUUID) {
          return;
        }
        setCartId(cartUUID);
      });
    }
  }, [dispatch, params.orderId]);

  return (
    <Layout backgroundColor="surface" currentStep={4}>
      <View gap={{ s: 10, m: 12 }}>
        <View
          as="section"
          direction={{ m: 'row' }}
          gap={{ s: 3, m: 7 }}
          align="start"
          attributes={{ style: { flexWrap: 'nowrap' } }}
        >
          <StatusIcon iconName="warning" />
          <View gap={4}>
            <View gap={1}>
              <Text
                as="h1"
                variant="body-1"
                weight="bold"
                // Override a style set in ReshapedGandiProvider.
                attributes={{ style: { margin: 0 } }}
              >
                {__('Your proforma document is ready!')}
              </Text>
              <Text as="p" variant="body-3" color="neutral-faded">
                {__(
                  'Your order is now available in the Billing section of your dashboard. If this order contains domain names, please note that no operation can be launched until your payment has been made and validated.',
                )}
              </Text>
            </View>
            <View direction={{ m: 'row' }} gap={{ s: 2, m: 3 }}>
              <Button
                href={pathjoin(
                  adminUrl,
                  'billing',
                  sharingId,
                  'proforma',
                  params.orderId,
                  'pdf',
                )}
                size="large"
                variant="outline"
                color="primary"
                icon={Download}
                attributes={{
                  target: '_blank',
                  rel: 'noopener',
                }}
              >
                {__('Download proforma')}
              </Button>
              <Button
                href={orderUrl}
                size="large"
                variant="outline"
                color="primary"
              >
                {__('View order')}
              </Button>
              {cartId && (
                <Button
                  to={`/cart/${cartId}/checkout/${params.orderId}`}
                  size="large"
                  color="primary"
                >
                  {__('Pay now')}
                </Button>
              )}
            </View>
          </View>
        </View>
        {(order || isFetching) && (
          <View
            gap={3}
            padding={6}
            borderRadius="large"
            backgroundColor="neutral-faded"
          >
            <View direction="row" wrap gap={2} justify="space-between">
              <Text variant="body-2" weight="bold">
                {__('Transaction number')}
              </Text>
              {order ? (
                <Text variant="body-2" weight="bold">
                  {order.id}
                </Text>
              ) : (
                <Skeleton width="4rem" />
              )}
            </View>

            <View direction="row" wrap gap={2} justify="space-between">
              <div>
                <Text as="span" variant="body-2" weight="bold">
                  {__('Total')}
                </Text>{' '}
                <Text as="span" variant="body-3" color="neutral-faded">
                  {__('(incl. VAT)')}
                </Text>
              </div>
              {order ? (
                <Text variant="body-2" weight="bold">
                  {order.price_after_taxes_label}
                </Text>
              ) : (
                <Skeleton width="3.3rem" />
              )}
            </View>
          </View>
        )}
        <CallToAction />
      </View>
    </Layout>
  );
}

export default (compose(
  withPageMeta(args => ({
    title: args.__('Order awaiting payment - Gandi.net'),
  })),
)(AwaitingPayment): React.ComponentType<{}>);
